import { globalUtils } from '@gat-ship/utils'
import {
  computed,
  ref,
  shallowRef,
  toValue,
  unref,
  watch,
  type MaybeRefOrGetter,
  type Ref
} from 'vue'
import { useRouter } from './useRouter'
import { useUnsavedChangesDialog } from './useUnsavedChangesDialog'

/**
 * Handles the beforeunload event to show a confirmation dialog.
 * @param {BeforeUnloadEvent} event - The beforeunload event.
 */
function handleBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault()
  event.returnValue = '' // Chrome requires returnValue to be set
}

export interface useUnsavedChangesOptions {
  /** If set to true and the user tries to close this tab/window a browser-controlled prompt will be shown to confirm their wish to close the tab. Not all browsers support this behaviour. */
  preventTabClose: boolean
}

export function useUnsavedChanges(
  /** Optional reactive Boolean variable used to override the internal state-tracker. If you are using this parameter then the setState function is no longer used. */
  unsavedChanges?: Ref<boolean>,
  options: useUnsavedChangesOptions = { preventTabClose: false }
) {
  /** The original value. This only mutates with the setState function. */
  const orgValue = shallowRef<unknown>(undefined)

  /** The new mutatable value. This is a direct reference to the Ref value sent in by the setState function. */
  const values = ref<unknown>(undefined)

  /** Use this function to track if a data-object has changed. NOTE: if you are using the first parameter of the composable then the internal state set by this function is no longer in use. */
  function setState(value: Ref<{ [key: string]: unknown }> | undefined) {
    if (value === undefined) {
      values.value = undefined
      orgValue.value = undefined
    } else {
      values.value = unref(value)
      // Create a copy.
      orgValue.value = globalUtils.functions.duplicateViaJson(unref(value))
    }
  }

  /** Returns true if the composable has any internal state. */
  const hasState = computed((): boolean => {
    if (
      (values.value && typeof values.value !== 'undefined') ||
      (orgValue.value && typeof orgValue.value !== 'undefined')
    ) {
      return true
    }
    return false
  })

  /** Computes if there are any changes between the initial state and the mutatable state. */
  const hasChanges = computed(() => {
    if (typeof unsavedChanges?.value === 'boolean') {
      return unsavedChanges.value
    }
    if (hasState.value) {
      return !globalUtils.functions.isIdenticalObjects(values.value, orgValue.value)
    }
    return false
  })

  /** The orgValue and values states. Should only be used for debugging.*/
  const _currentState = computed(() => ({ orgValue: orgValue.value, values: values.value }))

  // If the preventTabClose option is set, we need to add the event listener whenever there are differences between the orgValue and values object.
  if (options.preventTabClose) {
    watch(
      () => hasChanges.value,
      (val) => {
        if (val) {
          window.addEventListener('beforeunload', handleBeforeUnload)
        } else {
          window.removeEventListener('beforeunload', handleBeforeUnload)
        }
      }
    )
  }

  return {
    /** Use this function to track if a data-object has changed. NOTE: if you are using the first parameter of the composable then the internal state set by this function is no longer in use. */
    setState,
    /** Computes if there are any changes between the initial state and the mutatable state. */
    hasChanges,
    /** Returns true if the composable has any internal state. */
    hasState,
    /** The orgValue and values states. Should only be used for debugging.*/
    _currentState
  }
}

export type UnsavedChangesOptions = useUnsavedChangesOptions & {
  preventNavigationWhenOpen?: boolean
}

/** A composable meant for modals Must use the setState function to make use of unsaved changes.
 * @param isOpen - The isOpen reactive variable used to controll the open-state of the modals
 * @param options.preventTabClose - If set to true and the user tries to close this tab/window a browser-controlled prompt will be shown to confirm their wish to close the tab. Not all browsers support this behaviour. @default false
 * @param options.preventNavigationWhenOpen - If set to true the user cannot navigate using browser-controls while the modal is open @default true
 */
export function useUnsavedChangesModelValue(
  isOpen: Ref<boolean>,
  options: MaybeRefOrGetter<UnsavedChangesOptions> = {
    preventTabClose: false,
    preventNavigationWhenOpen: true
  }
) {
  const router = useRouter()

  const unsavedChanges = useUnsavedChanges(undefined, toValue(options))

  const { showUnsavedChangesDialog } = useUnsavedChangesDialog()

  watch(
    () => isOpen.value,
    (val) => {
      const _options = toValue(options)
      if (_options.preventNavigationWhenOpen) {
        // Prevents the ability to "go back" while the drawer is open
        router.forceStopNavigation.value = val
      }
      if (!val && unsavedChanges.hasChanges.value && _options.preventTabClose) {
        showUnsavedChangesDialog((discardChanges) => {
          if (discardChanges === 'no') {
            isOpen.value = true
          } else {
            unsavedChanges.setState(undefined)
          }
        })
      }
    }
  )

  return unsavedChanges
}
